import { useState, useCallback, useEffect } from 'react'
import req from '../api/request'
import { BillingScheduleItem } from '../types/Registration'

interface UserRegistrationImage {
  id: string
  filename: string
  full_path: string
  mime_type: string
  width: number
  height: number
  formats: {
    id: string
    filename: string
    full_path: string
    mime_type: string
    width: number
    height: number
  }[]
}

export interface UserRegistration {
  uid: string
  team: {
    uid: string
    team_player_roster_id: string
    name: string
    image: UserRegistrationImage | null
    is_bucket: boolean
  }
  schedule: {
    uid: string
    name: string
    start_date: string
    end_date: string
  }
  registration_type: string
  conditions: string | null
  condition_type: any[]
  status: string
  status_id: number
  is_comp: number
  billing: BillingScheduleItem[]
  created_at: string
  updated_at: string
  is_bucket: boolean
}

interface UserRegistrationState {
  registrations: UserRegistration[]
  loading: boolean
  error: string | null
}

interface UseUserRegistrationResult {
  registrationState: UserRegistrationState
  fetchUserRegistrations: (refresh?: boolean) => Promise<void>
}

export const useUserRegistrationData = (): UseUserRegistrationResult => {
  const [registrationState, setRegistrationState] =
    useState<UserRegistrationState>({
      registrations: [],
      loading: false,
      error: null,
    })

  const fetchUserRegistrations = useCallback(async (refresh = true) => {
    if (refresh) {
      setRegistrationState((prevState) => ({
        ...prevState,
        loading: true,
        error: null,
      }))
    }

    try {
      const { data } = await req('/users/me/registrations')

      setRegistrationState((prevState) => ({
        ...prevState,
        registrations: data,
        loading: false,
        error: null,
      }))
    } catch (e) {
      setRegistrationState((prevState) => ({
        ...prevState,
        loading: false,
        error: 'An error occurred when trying to fetch user registrations',
      }))
    }
  }, [])

  useEffect(() => {
    fetchUserRegistrations()
  }, [fetchUserRegistrations])

  return { registrationState, fetchUserRegistrations }
}
